import { useMemo, useState } from 'react';
import { dehlaPakadBestSuit, dehlaPakadPartnerOf, dehlaPakadTeamOf, sortHand, type DehlaPakadLegal, type DehlaPakadView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const isRedSuit = (s: string) => s === 'H' || s === 'D';

export function DehlaPakadTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as DehlaPakadView;
  const legal = useGame((s) => s.legal) as DehlaPakadLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const teamName = (team: number) => `${name(team)} & ${name(team + 2)}`;
  const myTeam = mySeat !== null ? dehlaPakadTeamOf(mySeat) : 0;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const choosing = view.phase === 'TRUMP';
  const playing = view.phase === 'PLAYING' || view.phase === 'TRICK_END';
  const trumpLabel = view.trump ? SUIT_SYMBOL[view.trump] : null;
  const teamTricks = (team: number) => (view.tricksWon[team] ?? 0) + (view.tricksWon[team + 2] ?? 0);
  const outcomeKey = (o: string) => `dp.outcome.${o}` as 'dp.outcome.KOT';

  const onCard = (c: string) => {
    if (!myTurn || legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  const teamPill = (team: number) => (
    <span className={`pill team-pill ${team === myTeam ? 'mine' : ''}`} key={team}>
      {team === myTeam ? t('sp.us') : t('sp.them')}: {view.teamScores[team] ?? 0}
      {playing && ` · ${teamTricks(team)} · 🔟 ${view.tensWon[team] ?? 0}`}
    </span>
  );

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.teamScores[dehlaPakadTeamOf(s)] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {mySeat !== null && s === dehlaPakadPartnerOf(mySeat) && <span className="tag">{t('sp.partner')}</span>}
                {playing && <span className="tag">{view.tricksWon[s] ?? 0}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('sp.round', { n: view.round })}
            {trumpLabel && (
              <>
                {' · '}
                {t('ta.trump')} <span style={{ color: isRedSuit(view.trump!) ? '#ff8a8a' : undefined, fontSize: '1.2em' }}>{trumpLabel}</span>
              </>
            )}
            {choosing && !myTurn && turn?.seats[0] !== undefined && ` · ${t('ta.choosing', { name: name(turn.seats[0]) })}`}
          </div>
          <div className="row" style={{ justifyContent: 'center', gap: 6 }} aria-label={t('table.scores')}>
            {teamPill(myTeam)}
            {teamPill(1 - myTeam)}
          </div>
          <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
            {view.trick.map((p, i) => {
              const dir = seatPosition(p.seat, mySeat, 4).dir;
              const o = OFFSETS[dir]!;
              const off = { x: o.x * k, y: o.y * k };
              const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 4).dir]! : null;
              const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
              return (
                <div
                  key={p.card}
                  className="slot fly"
                  style={{
                    transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1.5) * 4}deg)`,
                    ['--from-x' as string]: `${off.x * 3}px`,
                    ['--from-y' as string]: `${off.y * 4}px`,
                    ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                    zIndex: i,
                  }}
                >
                  <PlayingCard id={p.card} size={trickSize} />
                </div>
              );
            })}
          </div>
          {view.phase === 'ROUND_END' && view.roundResults.length > 0 && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('sp.roundScores', { n: view.round })}</strong>
              <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{t(outcomeKey(view.roundResults.at(-1)![0]!.outcome))}</div>
              <div className="row" style={{ justifyContent: 'center', marginTop: 6 }}>
                {view.roundResults.at(-1)!.map((r, team) => (
                  <span key={team} className={`pill ${r.delta > 0 ? 'accent' : ''}`}>
                    {team === myTeam ? t('sp.us') : t('sp.them')}: +{r.delta} · 🔟 {r.tens}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.teamScores[myTeam] ?? 0} pts</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && legal?.kind === 'trump' && <TrumpPicker suits={legal.suits} suggested={dehlaPakadBestSuit(view.hand)} onPick={(suit) => void act({ type: 'trump', suit })} />}
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={choosing ? i : undefined}
              selected={selected === c}
              playable={myTurn && playable.has(c)}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1].map((team) => (
                <th key={team} className="ellipsis" style={{ maxWidth: 110 }}>{teamName(team)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.roundResults.map((row, r) => (
              <tr key={r}>
                <td>R{r + 1}</td>
                {row.map((x, team) => (
                  <td key={team} className="num">
                    {x.delta > 0 ? `+${x.delta}` : '0'}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      🔟 {x.tens} · {x.tricks}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.teamScores.map((v, team) => (
                <td key={team} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('dp.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}

function TrumpPicker({ suits, suggested, onPick }: { suits: string[]; suggested: string; onPick: (suit: 'S' | 'H' | 'D' | 'C') => void }) {
  const t = useT();
  const [sent, setSent] = useState(false);
  return (
    <div className="bid-picker" role="group" aria-label={t('ta.chooseTrump')}>
      <div className="bid-head">
        <strong>{t('ta.chooseTrump')}</strong>
        <span className="dim">{t('dp.firstFive')} · ★ {t('cb.suggested')}: <b>{SUIT_SYMBOL[suggested]}</b></span>
      </div>
      <div className="bid-chips" role="group">
        {suits.map((s) => (
          <button
            key={s}
            className={`bid-chip ${s === suggested ? 'star' : ''}`}
            style={{ color: isRedSuit(s) ? 'var(--danger)' : undefined, fontSize: '1.6em' }}
            disabled={sent}
            aria-label={s === 'S' ? 'Spades' : s === 'H' ? 'Hearts' : s === 'D' ? 'Diamonds' : 'Clubs'}
            onClick={() => {
              setSent(true);
              onPick(s as 'S' | 'H' | 'D' | 'C');
              setTimeout(() => setSent(false), 1500);
            }}
          >
            {SUIT_SYMBOL[s]}
          </button>
        ))}
      </div>
    </div>
  );
}
